// Offer Card Component
// Custom web component for displaying offer cards

import type { OfferConfig } from "./offers-config.ts";
import { DISCOVERY_URL } from "../../utils/urls.ts";

class OfferCard extends HTMLElement {
  private config: OfferConfig | null = null;
  private addonsTotal: number = 0;

  connectedCallback() {
    // Get offer ID from attribute
    const offerId = this.getAttribute("offer-id");
    if (!offerId) {
      console.error("OfferCard: offer-id attribute is required");
      return;
    }

    // Load config will be handled by parent component
    this.render();
  }

  setConfig(config: OfferConfig) {
    this.config = config;
    this.render();
  }

  // Called externally when add-ons change
  updateAddonsTotal(total: number) {
    this.addonsTotal = total;
    this.updatePriceDisplay();
  }

  private updatePriceDisplay() {
    if (!this.config || typeof this.config.price !== "number") return;

    const totalPrice = this.config.price + this.addonsTotal;
    const priceElement = this.querySelector(".investment-price");
    if (priceElement) {
      priceElement.textContent = `€${totalPrice}`;
    }
  }

  private render() {
    if (!this.config) return;

    const basePrice = typeof this.config.price === "number"
      ? this.config.price
      : 0;
    const totalPrice = basePrice + this.addonsTotal;
    const priceDisplay = typeof this.config.price === "number"
      ? `€${totalPrice}`
      : this.config.price;
    const borderClass = this.config.borderStyle || "border-slate-200";
    const shadowClass = this.config.borderStyle ? "shadow-xl" : "shadow-lg";

    this.innerHTML = `
      <div class="pricing-card bg-white rounded-2xl ${shadowClass} border-2 ${borderClass} overflow-hidden flex flex-col h-full">
        <offer-card-header
          offer-id="${this.config.id}"
          gradient="${this.config.colorScheme.gradient}"
          primary="${this.config.colorScheme.primary}">
        </offer-card-header>

        <div class="p-8 flex flex-col flex-grow">
          <div style="min-height: 320px;">
            <h4 class="font-bold text-slate-900 mb-4 text-lg">
              <span data-en="What you get" data-lv="Ko jūs saņemat" data-it="Cosa ottieni">What you get</span>
            </h4>

            ${
      this.config.includesPrevious
        ? `<offer-includes-badges includes='${
          JSON.stringify(this.config.includesPrevious)
        }'></offer-includes-badges>`
        : ""
    }

            <offer-what-you-get
              offer-id="${this.config.id}"
              color="${this.config.colorScheme.primary}">
            </offer-what-you-get>
          </div>

          <div class="border-t pt-4 mb-6 flex-grow">
            <h4 class="font-bold text-slate-900 mb-4 text-lg">
              <span data-en="What we do" data-lv="Ko mēs darām" data-it="Cosa facciamo">What we do</span>
            </h4>

            <offer-what-we-do
              offer-id="${this.config.id}"
              color="${this.config.colorScheme.primary}">
            </offer-what-we-do>
          </div>

          ${
      this.config.showInvestmentSection
        ? `
          <div class="border-t pt-6 mb-6 mt-auto">
            <h4 class="font-bold text-slate-900 mb-3">
              <span data-en="What you invest" data-lv="Ko jūs ieguldāt" data-it="Cosa investi">What you invest</span>
            </h4>
            <div class="text-right">
              <span class="investment-price text-3xl font-bold text-${this.config.colorScheme.primary}">${priceDisplay}</span>
            </div>
          </div>
          `
        : ""
    }

          <div>
            <a href="${DISCOVERY_URL}" target="_blank" rel="noopener noreferrer"
              class="block w-full text-center px-6 py-3 ${this.config.colorScheme.button} text-white rounded-xl font-semibold transition-colors">
              <span data-en="Choose" data-lv="Izvēlēties" data-it="Scegli">Choose</span>
            </a>
          </div>
        </div>
      </div>
    `;

    // Re-apply language after rendering
    if (globalThis.switchLang) {
      const currentLang = localStorage.getItem("dammi_lang") || "en";
      globalThis.switchLang(currentLang);
    }
  }
}

customElements.define("offer-card", OfferCard);

export { OfferCard };
